import React from 'react';
import { useTranslation } from 'react-i18next';
import AssetDetailSection from './AssetDetailSection';
import DetailField from './DetailField';
import OpeningHoursSection from './OpeningHoursSection';
import InputField from '../../../components/ui/InputField';
import SelectField from '../../../components/ui/SelectField';
import SegmentedTabs from '../../../components/SegmentedTabs';
import { formatAddressLine } from '../../../utils/addressLine';
import type { HouseListItem } from '../../../api/station/stationApiTypes';
import type { OpeningHourEntry } from '../../../types/locations';
import { type UseCase, spaceOf } from '../../../utils/useCases';

export type RoomPlacement = 'new' | 'house';

interface RoomPlacementSectionProps {
    placement: RoomPlacement;
    onPlacementChange: (placement: RoomPlacement) => void;
    houses: HouseListItem[];
    housesLoading: boolean;
    /** The house list failed to load — said, never shown as "no house yet". */
    housesError: boolean;
    /**
     * The room's use case. Only a room booked by the hour (a venue) shows the
     * house's opening hours: a night-booked room's availability never reads them (see `showsOpeningHours` in
     * `AssetCreatePage`), so showing them would promise what booking ignores.
     */
    useCase: UseCase | null;
    houseId: string;
    onHouseChange: (houseId: string) => void;
    floor: string;
    onFloorChange: (floor: string) => void;
    /** Rendered for a new address: today's address and hours sections. */
    children: React.ReactNode;
}

/** The house's hours in the shape the opening-hours section reads. */
export const houseOpeningHours = (house: HouseListItem | undefined): OpeningHourEntry[] => (
    (house?.openingHours ?? [])
        .filter((hour) => typeof hour.weekday === 'string')
        .map((hour) => ({
            weekday: hour.weekday as OpeningHourEntry['weekday'],
            times: { fromHour: hour.fromHour, toHour: hour.toHour },
        }))
);

/**
 * Where the room stands (plan `room-houses-and-combining-rooms.md` §4 row 2):
 * at a new address — today's behaviour, its own location and one-room house —
 * or in an existing house.
 *
 * In a house the room takes the house's address and opening hours, both shown
 * read-only: the hours belong to the location every room there shares, and
 * editing them from one room would change them for all (A1 a). The floor stays
 * the room's own.
 */
const RoomPlacementSection = ({
    placement, onPlacementChange, houses, housesLoading, housesError, useCase, houseId, onHouseChange,
    floor, onFloorChange, children,
}: RoomPlacementSectionProps) => {
    const { t } = useTranslation();
    const house = houses.find((candidate) => candidate.id === houseId);
    const showsHours = spaceOf(useCase)?.timeUnit === 'hour';

    return (
        <>
            <AssetDetailSection title={t('asset_create.placement.title')}>
                <div className={'flex flex-col gap-4'}>
                    <SegmentedTabs<RoomPlacement>
                        items={[
                            { key: 'new', label: t('asset_create.placement.new_address') },
                            { key: 'house', label: t('asset_create.placement.in_house') },
                        ]}
                        value={placement}
                        onChange={onPlacementChange}
                    />
                    {placement === 'house' && (
                        <div className={'flex flex-col gap-4 max-w-xl'}>
                            <SelectField
                                label={`${t('asset_create.placement.house_label')} *`}
                                value={houseId}
                                onChange={onHouseChange}
                                options={houses.map((candidate) => candidate.id)}
                                optionLabels={Object.fromEntries(houses.map((candidate) => [
                                    candidate.id,
                                    t('asset_create.placement.house_option', {
                                        name: candidate.name,
                                        count: candidate.roomCount,
                                    }),
                                ]))}
                                placeholder={t('asset_create.placement.house_placeholder')}
                                noOptions={housesError
                                    ? t('asset_create.placement.houses_error')
                                    : t('asset_create.placement.no_houses')}
                                loading={housesLoading}
                            />
                            {house && (
                                <DetailField
                                    label={t('asset_create.placement.address')}
                                    value={formatAddressLine(house.address) || null}
                                />
                            )}
                            <InputField
                                label={t('asset_create.placement.floor')}
                                value={floor}
                                onChange={onFloorChange}
                            />
                            {showsHours && (
                                <p className={'text-sm text-foreground/60'}>
                                    {t('asset_create.placement.hours_inherited')}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </AssetDetailSection>

            {placement === 'house' && house && showsHours && (
                <OpeningHoursSection mode={'view'} openingHours={houseOpeningHours(house)} />
            )}

            {placement === 'new' && children}
        </>
    );
};

export default RoomPlacementSection;
